import type { RootState } from "~/app/store";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type {
  BundleQuoteGroup,
  EntryGroup,
  KitGroup,
  OrderableGroup,
  ProductPhaseoutGroup,
  QuotableGroup,
} from "~/features/cart/cart/dto/CartResponseDTO";
import {
  isBundleQuoteGroup,
  isKitGroup,
  isPackPhaseoutGroup,
  isProductPhaseoutGroup,
  isProductGroup,
} from "~/features/cart/cart/models/CartData";
import type { LineItem } from "~/features/cart/cart/models/CartData";
import type {
  AlternateRowViewModel,
  CartEntryRowViewModel,
  PhaseoutEntryMetadata,
  ProductTableMode,
  ProductTableRowViewModel,
  ProductTableViewModel,
} from "~/features/cart/cartV2/models/ProductTableViewModel";
import { buildCartEntryLines } from "~/features/cart/cartV2/utils/buildCartEntryLines";
import { buildCartEntrySummary } from "~/features/cart/cartV2/utils/buildCartEntrySummary";
import { buildEntryRowId } from "~/features/cart/cartV2/utils/buildTableId";
import {
  selectMainDescriptor,
  type ResolvedProductGroup,
} from "~/features/cart/cartV2/utils/selectResolvedGroupedEntryV2";

type TopLevelGroup = OrderableGroup | QuotableGroup | BundleQuoteGroup;

function buildChainBlocksFromGroup(group: TopLevelGroup): number[][] {
  const chainBlocks: number[][] = [];

  for (const child of group.children) {
    if (isProductPhaseoutGroup(child)) {
      const block = [...child.predecessorGroupNumbers, ...child.successorGroupNumbers];
      chainBlocks.push(block);
    }
  }

  return chainBlocks;
}

function buildOrderedProductGroupNumbers(group: TopLevelGroup): number[] {
  return group.children.flatMap((child) => {
    if (isProductGroup(child)) {
      return [child.groupNumber];
    }
    if (isProductPhaseoutGroup(child)) {
      return [...child.predecessorGroupNumbers, ...child.successorGroupNumbers];
    }
    return [];
  });
}

function findParentPhaseoutGroup(group: TopLevelGroup, productGroupNumber: number): ProductPhaseoutGroup | null {
  for (const child of group.children) {
    if (isProductPhaseoutGroup(child)) {
      const inChain =
        child.predecessorGroupNumbers.includes(productGroupNumber) ||
        child.successorGroupNumbers.includes(productGroupNumber);
      if (inChain) {
        return child;
      }
    }
  }
  return null;
}

function buildEntryPhaseoutMetadata(
  productGroupNumber: number,
  parentPhaseoutGroup: ProductPhaseoutGroup | null,
  lineItemPks: number[],
): PhaseoutEntryMetadata | undefined {
  if (!parentPhaseoutGroup) {
    return undefined;
  }

  const isPredecessor = parentPhaseoutGroup.predecessorGroupNumbers.includes(productGroupNumber);
  const isSuccessor = parentPhaseoutGroup.successorGroupNumbers.includes(productGroupNumber);

  if (!isPredecessor && !isSuccessor) {
    return undefined;
  }

  const lockedPks = new Set(parentPhaseoutGroup.predecessorPackEntryPks ?? []);
  const hasLockedLines = lineItemPks.some((pk) => lockedPks.has(pk));

  const allGroupNums = [...parentPhaseoutGroup.predecessorGroupNumbers, ...parentPhaseoutGroup.successorGroupNumbers];
  const chainPosition = allGroupNums.indexOf(productGroupNumber);
  const chainLength = allGroupNums.length;

  return {
    isPredecessor,
    isSuccessor,
    chainPosition,
    chainLength,
    hasLockedLines,
  };
}

function isKitOptionalPart(lineItem: LineItem, entryGroupsByNumber: Record<number, EntryGroup>): boolean {
  for (const groupNumber of lineItem.groupNumbers) {
    const group = entryGroupsByNumber[groupNumber];
    if (group && isKitGroup(group)) {
      const kitGroup = group as KitGroup;
      return lineItem.pk !== kitGroup.housingEntryPk;
    }
  }
  return false;
}

function splitLineItems(
  lineItems: LineItem[],
  entryGroupsByNumber: Record<number, EntryGroup>,
): { primaryLineItems: LineItem[]; kitOptionalLineItems: LineItem[] } {
  const primaryLineItems: LineItem[] = [];
  const kitOptionalLineItems: LineItem[] = [];
  for (const li of lineItems) {
    if (isKitOptionalPart(li, entryGroupsByNumber)) {
      kitOptionalLineItems.push(li);
    } else {
      primaryLineItems.push(li);
    }
  }
  return { primaryLineItems, kitOptionalLineItems };
}

/**
 * Per-group cache for entry row view models.
 * Returns the cached result when the `resolvedGroup` reference is unchanged,
 * avoiding new object allocations for `CartEntry` components whose data hasn't changed.
 * Stale entries (deleted groups) are evicted inside `buildProductTableViewModel` before the loop.
 */
const entryRowCache = new Map<
  number,
  {
    resolvedGroup: ResolvedProductGroup;
    entryGroupsByNumber: Record<number, EntryGroup>;
    result: CartEntryRowViewModel;
  }
>();

function buildEntryRowViewModel(args: {
  resolvedGroup: ResolvedProductGroup;
  mode: ProductTableMode;
  lineItemsByGroupNumber: Record<number, LineItem[]>;
  optionalPartDescriptors: Record<number, ProductDescriptor>;
  phaseout?: PhaseoutEntryMetadata;
  lockedPackEntryPks: ReadonlySet<number>;
  entryGroupsByNumber: Record<number, EntryGroup>;
  packPhaseoutPredecessorPks?: ReadonlySet<number>;
}): CartEntryRowViewModel {
  const { resolvedGroup, entryGroupsByNumber } = args;
  const groupNumber = resolvedGroup.productGroupNumber;

  // Fast path: if both resolvedGroup and entryGroupsByNumber references are unchanged,
  // all derived data (descriptors, line items, phaseout metadata) is also unchanged.
  const cached = entryRowCache.get(groupNumber);
  if (cached && cached.resolvedGroup === resolvedGroup && cached.entryGroupsByNumber === entryGroupsByNumber) {
    return cached.result;
  }

  const { mode, optionalPartDescriptors, phaseout } = args;
  const { primaryLineItems } = splitLineItems(resolvedGroup.lineItems, entryGroupsByNumber);
  const primaryResolvedGroup = { ...resolvedGroup, lineItems: primaryLineItems };
  const primaryMainDescriptorSelection = selectMainDescriptor(resolvedGroup.descriptors, primaryLineItems);

  const result: CartEntryRowViewModel = {
    kind: "entry",
    rowId: buildEntryRowId(resolvedGroup.productGroupNumber),
    cartEntryId: resolvedGroup.productGroupNumber,
    productGroupNumber: resolvedGroup.productGroupNumber,
    grouping: {
      cartEntryId: resolvedGroup.productGroupNumber,
      packGroups: [{ lineItemIds: primaryLineItems.map((li) => li.id) }],
    },
    descriptors: resolvedGroup.descriptors,
    mainDescriptor: primaryMainDescriptorSelection.descriptor,
    mainDescriptorSelection: primaryMainDescriptorSelection,
    summary: buildCartEntrySummary(primaryResolvedGroup, phaseout?.hasLockedLines ?? false),
    details: buildCartEntryLines({
      resolvedGroup: primaryResolvedGroup,
      mode,
      optionalPartDescriptors,
      lockedPackEntryPks: args.lockedPackEntryPks,
      lineItemsByGroupNumber: args.lineItemsByGroupNumber,
      entryGroupsByNumber,
      packPhaseoutPredecessorPks: args.packPhaseoutPredecessorPks,
    }),
    phaseout,
  };

  entryRowCache.set(groupNumber, { resolvedGroup, entryGroupsByNumber, result });
  return result;
}

function buildChainRowId(chainIndex: number): string {
  return `cart-chain:${chainIndex}`;
}

function buildChainBlockIndexByEntryId(chainBlocks: ReadonlyArray<ReadonlyArray<number>>): Map<number, number> {
  const chainBlockIndexByEntryId = new Map<number, number>();
  for (let blockIdx = 0; blockIdx < chainBlocks.length; blockIdx++) {
    for (const entryId of chainBlocks[blockIdx]) {
      chainBlockIndexByEntryId.set(entryId, blockIdx);
    }
  }
  return chainBlockIndexByEntryId;
}

function buildChainEntryIds(chainBlocks: ReadonlyArray<ReadonlyArray<number>>): Set<number> {
  const chainEntryIds = new Set<number>();
  for (const block of chainBlocks) {
    for (const entryId of block) {
      chainEntryIds.add(entryId);
    }
  }
  return chainEntryIds;
}

function partitionAlternateRows(
  alternateRows: AlternateRowViewModel[],
  chainEntryIds: Set<number>,
): {
  deferredAlternatesByAnchor: Record<string, AlternateRowViewModel[]>;
  normalAlternateByAnchor: Record<string, AlternateRowViewModel[]>;
} {
  const deferredAlternatesByAnchor: Record<string, AlternateRowViewModel[]> = {};
  const normalAlternateByAnchor: Record<string, AlternateRowViewModel[]> = {};

  for (const row of alternateRows) {
    const anchorId = row.anchorProductGroupNumber;
    const target =
      anchorId !== null && chainEntryIds.has(anchorId) ? deferredAlternatesByAnchor : normalAlternateByAnchor;
    const key = String(anchorId);

    if (!target[key]) {
      target[key] = [];
    }

    target[key].push(row);
  }

  return { deferredAlternatesByAnchor, normalAlternateByAnchor };
}

function buildDeferredKeysByBlock(
  alternateRows: AlternateRowViewModel[],
  chainEntryIds: Set<number>,
  chainBlockIndexByEntryId: Map<number, number>,
): Map<number, Set<string>> {
  const deferredKeysByBlock = new Map<number, Set<string>>();

  for (const row of alternateRows) {
    const anchorId = row.anchorProductGroupNumber;
    if (anchorId === null || !chainEntryIds.has(anchorId)) {
      continue;
    }

    const blockIdx = chainBlockIndexByEntryId.get(anchorId);
    if (blockIdx === undefined) {
      continue;
    }

    if (!deferredKeysByBlock.has(blockIdx)) {
      deferredKeysByBlock.set(blockIdx, new Set());
    }

    deferredKeysByBlock.get(blockIdx)!.add(`${anchorId}:${row.productCode}`);
  }

  return deferredKeysByBlock;
}

function buildRows(args: {
  entryRows: CartEntryRowViewModel[];
  alternateRows: AlternateRowViewModel[];
  chainBlocks: ReadonlyArray<ReadonlyArray<number>>;
}): ProductTableRowViewModel[] {
  const { entryRows, alternateRows, chainBlocks } = args;

  if (entryRows.length === 0) {
    return alternateRows;
  }

  const chainBlockIndexByEntryId = buildChainBlockIndexByEntryId(chainBlocks);
  const chainEntryIds = buildChainEntryIds(chainBlocks);
  const { deferredAlternatesByAnchor, normalAlternateByAnchor } = partitionAlternateRows(alternateRows, chainEntryIds);
  const deferredKeysByBlock = buildDeferredKeysByBlock(alternateRows, chainEntryIds, chainBlockIndexByEntryId);

  const rows: ProductTableRowViewModel[] = [];
  const flushedDeferredKeys = new Set<string>();

  const flushDeferredForBlock = (blockIdxToFlush: number) => {
    const blockKeys = deferredKeysByBlock.get(blockIdxToFlush);
    if (!blockKeys) {
      return;
    }
    for (const key of blockKeys) {
      if (!flushedDeferredKeys.has(key)) {
        flushedDeferredKeys.add(key);
        const [anchorStr] = key.split(":");
        const deferred = deferredAlternatesByAnchor[anchorStr];
        if (deferred) {
          const match = deferred.find((r) => `${r.anchorProductGroupNumber}:${r.productCode}` === key);
          if (match) {
            rows.push(match);
          }
        }
      }
    }
  };

  let lastChainBlockIdx = -1;
  let chainRowCounter = 0;
  let lastChainEntryId: number | null = null;

  for (let i = 0; i < entryRows.length; i++) {
    const entryRow = entryRows[i];
    const entryId = entryRow.productGroupNumber;
    const blockIdx = chainBlockIndexByEntryId.get(entryId) ?? -1;

    // When transitioning away from a chain block, flush deferred alternates
    // before anything else so they appear between the block and the next entry.
    if (lastChainBlockIdx !== -1 && blockIdx !== lastChainBlockIdx) {
      flushDeferredForBlock(lastChainBlockIdx);
      lastChainBlockIdx = -1;
      lastChainEntryId = null;
    }

    // Insert chain row between adjacent entries in the same block
    if (blockIdx !== -1 && blockIdx === lastChainBlockIdx && lastChainEntryId !== null) {
      rows.push({
        kind: "chain",
        rowId: buildChainRowId(chainRowCounter++),
      });
    }

    // Normal alternates anchored to this entry (before chain members too)
    rows.push(...(normalAlternateByAnchor[String(entryId)] ?? []));

    rows.push(entryRow);

    if (blockIdx !== -1) {
      lastChainBlockIdx = blockIdx;
      lastChainEntryId = entryId;
    }
  }

  // Flush any remaining deferred alternates for the last chain block in the table
  if (lastChainBlockIdx !== -1) {
    flushDeferredForBlock(lastChainBlockIdx);
  }

  return rows;
}

export interface BuildProductTableViewModelArgs {
  group: TopLevelGroup;
  mode: ProductTableMode;
  resolvedProductGroups: ResolvedProductGroup[];
  lineItemsByGroupNumber: Record<number, LineItem[]>;
  optionalPartDescriptors: Record<number, ProductDescriptor>;
  alternateRows: AlternateRowViewModel[];
  entryGroupsByNumber: Record<number, EntryGroup>;
}

export function buildProductTableViewModel({
  group,
  mode,
  resolvedProductGroups,
  lineItemsByGroupNumber,
  optionalPartDescriptors,
  alternateRows,
  entryGroupsByNumber,
}: BuildProductTableViewModelArgs): ProductTableViewModel {
  const tableId = `table:${group.groupType}:${group.groupNumber}:${isBundleQuoteGroup(group) ? group.quoteNumber : ""}`;

  const resolvedByGroupNumber = new Map<number, ResolvedProductGroup>();
  for (const rg of resolvedProductGroups) {
    resolvedByGroupNumber.set(rg.productGroupNumber, rg);
  }

  // Evict cached entry row VMs for groups that no longer exist in the current table
  for (const key of entryRowCache.keys()) {
    if (!resolvedByGroupNumber.has(key)) {
      entryRowCache.delete(key);
    }
  }

  const orderedGroupNumbers = buildOrderedProductGroupNumbers(group);
  const chainBlocks = buildChainBlocksFromGroup(group);

  const entryRows: CartEntryRowViewModel[] = [];
  for (const groupNumber of orderedGroupNumbers) {
    const resolvedGroup = resolvedByGroupNumber.get(groupNumber);
    if (!resolvedGroup) {
      continue;
    }

    const parentPhaseoutGroup = findParentPhaseoutGroup(group, groupNumber);
    const lockedPackEntryPks = new Set<number>(parentPhaseoutGroup?.predecessorPackEntryPks ?? []);

    // Same-product (pack) phaseout locks
    const packPhaseoutChild = resolvedGroup.productGroup.children.find(isPackPhaseoutGroup);
    if (packPhaseoutChild) {
      for (const pk of packPhaseoutChild.predecessorPackEntryPks) {
        lockedPackEntryPks.add(pk);
      }
    }

    entryRows.push(
      buildEntryRowViewModel({
        resolvedGroup,
        mode,
        lineItemsByGroupNumber,
        optionalPartDescriptors,
        phaseout: buildEntryPhaseoutMetadata(
          groupNumber,
          parentPhaseoutGroup,
          resolvedGroup.lineItems.map((li) => li.pk),
        ),
        lockedPackEntryPks,
        entryGroupsByNumber,
        packPhaseoutPredecessorPks: packPhaseoutChild ? new Set(packPhaseoutChild.predecessorPackEntryPks) : undefined,
      }),
    );
  }

  const rows = buildRows({ entryRows, alternateRows, chainBlocks });

  return {
    tableId,
    mode,
    groupType: group.groupType,
    quoteNumber: isBundleQuoteGroup(group) ? group.quoteNumber : undefined,
    rows,
  };
}

export function buildOptionalPartDescriptorMap(
  state: RootState,
  resolvedProductGroups: ResolvedProductGroup[],
): Record<number, ProductDescriptor> {
  const map: Record<number, ProductDescriptor> = {};
  for (const rg of resolvedProductGroups) {
    for (const li of rg.lineItems) {
      map[li.id] = selectProductDescriptor(state, {
        productCode: li.productCode,
        priceReference: li.priceReference,
      });
    }
  }
  return map;
}

export function compareOptionalPartDescriptorMaps(
  prev: Record<number, ProductDescriptor>,
  next: Record<number, ProductDescriptor>,
): boolean {
  const prevKeys = Object.keys(prev);
  if (prevKeys.length !== Object.keys(next).length) {
    return false;
  }
  return prevKeys.every((key) => prev[key as unknown as number] === next[key as unknown as number]);
}
